import type { Session, User } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AppPlatform } from './platform.ts'
import { TERMS_VERSION } from './lib/terms.ts'

type SessionState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; user: User; session: Session }

interface SessionApi {
  state: SessionState
  platform: AppPlatform
  signIn(email: string, password: string): Promise<void>
  signUp(email: string, password: string, fullName: string): Promise<{ needsConfirmation: boolean }>
  /** Ends the Supabase session only; the workspace deletes local data first. */
  signOutAuth(): Promise<void>
  /** Signs in with the 6-digit code emailed to a new account. */
  verifySignUpCode(email: string, code: string): Promise<void>
  /** Emails a new account another code. */
  resendSignUpCode(email: string): Promise<void>
  /**
   * Emails a 6-digit code to reset the password, or a link to /reset-password where the
   * project's template still sends one (says nothing about whether the account exists).
   */
  sendPasswordReset(email: string): Promise<void>
  /** Signs in with the reset code, so that a new password can be set. */
  verifyResetCode(email: string, code: string): Promise<void>
  /**
   * Sets a new password for the signed-in user (after the reset code or
   * link), optionally ending the account's sessions on every other device.
   */
  updatePassword(password: string, options?: { signOutOthers?: boolean }): Promise<void>
  /**
   * Changes the signed-in person's password from inside the app. The current password is
   * checked first (a `WrongPasswordError` when it is not theirs, and nothing changes).
   */
  changePassword(
    current: string,
    next: string,
    options?: { signOutOthers?: boolean },
  ): Promise<void>
  /** Throws `WrongPasswordError` unless this is the signed-in person's password. */
  verifyPassword(password: string): Promise<void>
  /** Changes the signed-in person's own name, for their team and on this device. */
  updateName(name: string): Promise<void>
  /** The Terms this person accepted, and the current ones (docs/plans/terms.md). */
  myTerms(): Promise<{
    accepted_version: string | null
    accepted_at: string | null
    current_version: string
  }>
  /** Accepts the current Terms for this person; the server records when. */
  acceptTerms(version: string): Promise<void>
}

/** The password typed as the current one is not the account's. */
export class WrongPasswordError extends Error {
  constructor() {
    super('That is not your current password.')
    this.name = 'WrongPasswordError'
  }
}

/** Where the password-reset email sends people (must be an allowed Supabase redirect URL). */
export const RESET_PASSWORD_PATH = '/reset-password'

const SessionContext = createContext<SessionApi | null>(null)

export function SessionProvider({
  platform,
  children,
}: {
  platform: AppPlatform
  children: ReactNode
}) {
  const [state, setState] = useState<SessionState>({ status: 'loading' })
  const { supabase } = platform

  useEffect(() => {
    // getSession reads the persisted session, so this also works offline.
    void supabase.auth.getSession().then(({ data }) => {
      setState(
        data.session
          ? { status: 'signedIn', user: data.session.user, session: data.session }
          : { status: 'signedOut' },
      )
    })
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setState(
        session ? { status: 'signedIn', user: session.user, session } : { status: 'signedOut' },
      )
    })
    return () => data.subscription.unsubscribe()
  }, [supabase])

  const api = useMemo<SessionApi>(() => {
    const api: SessionApi = {
      state,
      platform,
      async signIn(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      },
      async signUp(email, password, fullName) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          // The Terms the form showed; the server records them with its own time.
          options: { data: { full_name: fullName, terms_version: TERMS_VERSION } },
        })
        if (error) throw error
        return { needsConfirmation: !data.session }
      },
      async verifySignUpCode(email, code) {
        const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'signup' })
        if (error) throw error
      },
      async resendSignUpCode(email) {
        const { error } = await supabase.auth.resend({ type: 'signup', email })
        if (error) throw error
      },
      async verifyResetCode(email, code) {
        const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' })
        if (error) throw error
      },
      async signOutAuth() {
        // A shared phone stops getting this person's restaurant alerts (best effort).
        try {
          const endpoint = await platform.push?.unsubscribe()
          if (endpoint) await supabase.rpc('push_unsubscribe', { p_endpoint: endpoint })
        } catch {
          // Offline or no service worker: the server forgets it once the push service does.
        }
        await supabase.auth.signOut()
      },
      async sendPasswordReset(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}${RESET_PASSWORD_PATH}`,
        })
        if (error) throw error
      },
      async updatePassword(password, options = {}) {
        const { error } = await supabase.auth.updateUser({ password })
        if (error) throw error
        if (options.signOutOthers) {
          const { error: signOutError } = await supabase.auth.signOut({ scope: 'others' })
          if (signOutError) throw signOutError
        }
      },
      async verifyPassword(password) {
        const email = state.status === 'signedIn' ? state.user.email : undefined
        if (!email) throw new Error('not signed in')
        // Signing in again is the check: the server has no "is this my password" call.
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) {
          if (error.code === 'invalid_credentials' || error.status === 400) {
            throw new WrongPasswordError()
          }
          throw error
        }
      },
      async changePassword(current, next, options = {}) {
        await api.verifyPassword(current)
        await api.updatePassword(next, options)
      },
      async updateName(name) {
        // The profile is what the team sees; the account's own copy is what this device
        // shows offline (it travels in the saved session).
        const { data, error } = await supabase.rpc('set_my_name', { p_name: name })
        if (error) throw error
        const { error: metaError } = await supabase.auth.updateUser({
          data: { full_name: (data as string | null) ?? name },
        })
        if (metaError) throw metaError
      },
      async myTerms() {
        const { data, error } = await supabase.rpc('my_terms')
        if (error) throw error
        return data as Awaited<ReturnType<SessionApi['myTerms']>>
      },
      async acceptTerms(version) {
        const { error } = await supabase.rpc('accept_terms', { p_version: version })
        if (error) throw error
      },
    }
    return api
  }, [state, platform, supabase])

  return <SessionContext.Provider value={api}>{children}</SessionContext.Provider>
}

export function useSession(): SessionApi {
  const api = useContext(SessionContext)
  if (!api) throw new Error('useSession must be used inside <SessionProvider>')
  return api
}

export function useUser(): User {
  const { state } = useSession()
  if (state.status !== 'signedIn') throw new Error('not signed in')
  return state.user
}
